import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import apiClient from "../api";

const Badge = ({ children, tone = "gray" }) => {
  const tones = {
    gray: "bg-gray-100 text-gray-700 border-gray-200",
    green: "bg-green-100 text-green-700 border-green-200",
    yellow: "bg-yellow-100 text-yellow-700 border-yellow-200",
    blue: "bg-blue-100 text-blue-700 border-blue-200",
    purple: "bg-purple-100 text-purple-700 border-purple-200",
    red: "bg-red-100 text-red-700 border-red-200",
  };

  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${
        tones[tone] || tones.gray
      }`}
    >
      {children}
    </span>
  );
};

const InfoRow = ({ label, value, mono = false }) => (
  <div className="flex items-start justify-between gap-4 py-2 border-b border-gray-100 last:border-b-0">
    <span className="text-sm font-medium text-gray-600 min-w-[140px]">{label}</span>
    <span
      className={`text-sm text-gray-900 text-right break-all ${
        mono ? "font-mono text-[13px]" : ""
      }`}
    >
      {value ?? "-"}
    </span>
  </div>
);

const SectionCard = ({ title, subtitle, children, icon }) => (
  <div className="bg-white border rounded-2xl p-5 shadow-sm">
    <div className="mb-4">
      <h2 className="text-lg font-semibold text-gray-900">
        {icon ? `${icon} ${title}` : title}
      </h2>
      {subtitle ? <p className="text-sm text-gray-500 mt-1">{subtitle}</p> : null}
    </div>
    {children}
  </div>
);

const getPaymentTone = (status) => {
  switch (status) {
    case "Paid":
      return "green";
    case "Pending":
      return "yellow";
    case "Manual":
      return "blue";
    case "PaidToOperator":
      return "purple";
    default:
      return "gray";
  }
};

const getStateTone = (status) => {
  switch (String(status || "").toUpperCase()) {
    case "CONFIRMED":
    case "SUCCESS":
    case "PAID":
      return "green";
    case "PENDING":
    case "PROCESSING":
      return "yellow";
    case "FAILED":
    case "CANCELLED":
    case "DECLINED":
      return "red";
    default:
      return "gray";
  }
};

const getObjectId = (value) => {
  if (!value) return "-";
  if (typeof value === "string") return value;
  if (typeof value === "object" && value._id) return value._id;
  return "-";
};

const AdminBookingView = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchBooking = async () => {
      try {
        setLoading(true);
        setError("");

        const token =
          localStorage.getItem("token") || localStorage.getItem("authToken") || "";

        const res = await apiClient.get(`/bookings/admin/bookings/${id}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        setBooking(res.data);
      } catch (err) {
        console.error(err);
        setError(err?.response?.data?.message || "Failed to load booking details");
      } finally {
        setLoading(false);
      }
    };

    fetchBooking();
  }, [id]);

  const formatDate = (d) => (d ? new Date(d).toLocaleString("en-LK") : "-");

  const seats = booking?.selectedSeats || [];
  const seatAllocations = booking?.seatAllocations || [];
  const passengers = booking?.passengers || [];
  const passengerInfo = booking?.passengerInfo || {};
  const payment = booking?.payment || {};
  const bus = booking?.bus || {};
  const user = booking?.user || {};
  const bookedBy = booking?.bookedBy || {};

  const primaryName =
    passengerInfo.fullName || user.fullName || user.name || bookedBy.fullName || "-";
  const primaryPhone = passengerInfo.phone || user.mobile || "-";
  const primaryEmail = passengerInfo.email || user.email || "-";
  const primaryNic = passengerInfo.nic || user.nic || "-";

  const bookingStatus = booking?.status || booking?.paymentStatus || "-";
  const seatCount = Array.isArray(seats) ? seats.length : 0;
  const passengerCount = Array.isArray(passengers) ? passengers.length : 0;

  const bookingOwnerId = getObjectId(booking?.user) !== "-" ? getObjectId(booking?.user) : getObjectId(user);
  const busObjectId = getObjectId(booking?.bus) !== "-" ? getObjectId(booking?.bus) : getObjectId(bus);
  const operatorId = getObjectId(bus?.operator);

  const derivedWarnings = useMemo(() => {
    const warnings = [];

    if (!primaryPhone || primaryPhone === "N/A" || primaryPhone === "-") {
      warnings.push("Primary phone number is missing.");
    }

    if (!primaryEmail || primaryEmail === "N/A" || primaryEmail === "-") {
      warnings.push("Primary email is missing.");
    }

    if (!primaryNic || primaryNic === "N/A" || primaryNic === "-") {
      warnings.push("NIC/ID is missing.");
    }

    if (seatCount > 0 && passengerCount > 0 && seatCount !== passengerCount) {
      warnings.push("Passenger count does not match selected seat count.");
    }

    if (booking?.paymentStatus === "Pending") {
      warnings.push("This booking is still pending payment.");
    }

    if (booking?.isManual) {
      warnings.push("This is a manual/operator-created booking.");
    }

    if (
      booking?.paymentStatus === "Paid" &&
      payment?.state &&
      !["CONFIRMED", "SUCCESS", "PAID"].includes(String(payment.state).toUpperCase())
    ) {
      warnings.push("Paid booking has a payment state that may need verification.");
    }

    return warnings;
  }, [primaryPhone, primaryEmail, primaryNic, seatCount, passengerCount, booking, payment]);

  if (loading) {
    return <div className="p-6">Loading booking...</div>;
  }

  if (error) {
    return <div className="p-6 text-red-600">{error}</div>;
  }

  if (!booking) {
    return <div className="p-6">No booking found</div>;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl p-6 mb-6 shadow-sm">
        <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-5">
          <div>
            <p className="text-sm text-blue-100 mb-1">Booking Overview</p>
            <h1 className="text-2xl md:text-3xl font-bold">
              {booking.bookingNo || booking.bookingNoShort || booking._id}
            </h1>
            <p className="text-sm text-blue-100 mt-2">
              Complete booking, customer, trip, and payment details
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Badge tone={getPaymentTone(booking.paymentStatus)}>
              Payment: {booking.paymentStatus || "-"}
            </Badge>
            <Badge tone={booking.isManual ? "blue" : "green"}>
              {booking.isManual ? "Manual Booking" : "Online Booking"}
            </Badge>
            <Badge tone={getStateTone(payment.state)}>
              Gateway: {payment.state || "-"}
            </Badge>
            <Badge tone={booking.operatorPaymentStatus === "Paid" ? "purple" : "gray"}>
              Operator Settlement: {booking.operatorPaymentStatus || "-"}
            </Badge>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 mt-6">
          <div className="rounded-xl bg-white/10 border border-white/15 p-4">
            <p className="text-xs uppercase tracking-wide text-blue-100">Passenger</p>
            <p className="text-base font-semibold mt-1 break-words">{primaryName}</p>
            <p className="text-sm text-blue-100 mt-1 break-all">{primaryPhone}</p>
          </div>
          <div className="rounded-xl bg-white/10 border border-white/15 p-4">
            <p className="text-xs uppercase tracking-wide text-blue-100">Route</p>
            <p className="text-base font-semibold mt-1">
              {bus.from || "-"} → {bus.to || "-"}
            </p>
            <p className="text-sm text-blue-100 mt-1">{bus.name || "-"}</p>
          </div>
          <div className="rounded-xl bg-white/10 border border-white/15 p-4">
            <p className="text-xs uppercase tracking-wide text-blue-100">Travel</p>
            <p className="text-base font-semibold mt-1">{booking.date || "-"}</p>
            <p className="text-sm text-blue-100 mt-1">{booking.departureTime || "-"}</p>
          </div>
          <div className="rounded-xl bg-white/10 border border-white/15 p-4">
            <p className="text-xs uppercase tracking-wide text-blue-100">Seats</p>
            <p className="text-base font-semibold mt-1">
              {seatCount > 0 ? seats.join(", ") : "-"}
            </p>
            <p className="text-sm text-blue-100 mt-1">
              {seatCount} selected · {passengerCount} passengers
            </p>
          </div>
        </div>
      </div>

      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          <a
            href={primaryPhone && primaryPhone !== "-" ? `tel:${primaryPhone}` : undefined}
            className={`px-4 py-2 rounded-lg border text-sm font-medium ${
              primaryPhone && primaryPhone !== "-"
                ? "bg-green-50 text-green-700 border-green-200 hover:bg-green-100"
                : "bg-gray-50 text-gray-400 border-gray-200 pointer-events-none"
            }`}
          >
            Call Customer
          </a>
          <button
            type="button"
            onClick={() => navigator.clipboard?.writeText(String(primaryPhone || ""))}
            className="px-4 py-2 rounded-lg border bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Copy Phone
          </button>
          <button
            type="button"
            onClick={() => window.print()}
            className="px-4 py-2 rounded-lg border bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Print
          </button>
        </div>

        <button
          onClick={() => navigate("/admin/bookings")}
          className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 text-sm font-medium"
        >
          ← Back to Bookings
        </button>
      </div>

      <div className="mb-6">
        <SectionCard
          title="Alerts & Admin Notes"
          subtitle="Quick checks that help you understand the booking at a glance"
          icon="⚠️"
        >
          {derivedWarnings.length ? (
            <div className="space-y-2">
              {derivedWarnings.map((item, index) => (
                <div
                  key={index}
                  className="rounded-xl border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800"
                >
                  {item}
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
              No immediate warnings detected for this booking.
            </div>
          )}
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard
          title="Booking Information"
          subtitle="Core booking status, timestamps, and amount details"
          icon="📄"
        >
          <div>
            <InfoRow label="Booking No" value={booking.bookingNo || "-"} mono />
            <InfoRow label="Short Booking No" value={booking.bookingNoShort || "-"} mono />
            <InfoRow label="Booking ID" value={booking._id || "-"} mono />
            <InfoRow label="Booking Status" value={bookingStatus} />
            <InfoRow label="Payment Status" value={booking.paymentStatus || "-"} />
            <InfoRow label="Manual Booking" value={booking.isManual ? "Yes" : "No"} />
            <InfoRow label="Booked At" value={formatDate(booking.createdAt)} />
            <InfoRow label="Updated At" value={formatDate(booking.updatedAt)} />
            <InfoRow label="Paid At" value={formatDate(payment.paidAt)} />
            <InfoRow
              label="Operator Settlement"
              value={booking.operatorPaymentStatus || "-"}
            />
            <InfoRow
              label="Total Amount"
              value={`Rs. ${Number(booking.totalAmount || 0).toLocaleString()}`}
            />
            <InfoRow
              label="Convenience Fee"
              value={`Rs. ${Number(booking.convenienceFee || 0).toLocaleString()}`}
            />
            <InfoRow
              label="Estimated Base Fare"
              value={`Rs. ${Math.max(
                Number(booking.totalAmount || 0) - Number(booking.convenienceFee || 0),
                0
              ).toLocaleString()}`}
            />
          </div>
        </SectionCard>

        <SectionCard
          title="Passenger / Emergency Information"
          subtitle="Primary contact information used for support and verification"
          icon="👤"
        >
          <div>
            <InfoRow label="Full Name" value={primaryName} />
            <InfoRow label="Phone" value={primaryPhone} />
            <InfoRow label="Email" value={primaryEmail} />
            <InfoRow label="NIC / ID" value={primaryNic} />
            <InfoRow label="User Account Name" value={user.fullName || user.name || "-"} />
            <InfoRow label="User Account Email" value={user.email || "-"} />
            <InfoRow label="User Mobile" value={user.mobile || "-"} />
            <InfoRow label="User Role" value={user.role || "-"} />
            <InfoRow label="User ID" value={getObjectId(user)} mono />
          </div>
        </SectionCard>

        <SectionCard
          title="Trip Details"
          subtitle="Route, bus, and boarding details for this journey"
          icon="🚌"
        >
          <div>
            <InfoRow label="Bus Name" value={bus.name || "-"} />
            <InfoRow label="Bus Type" value={bus.busType || "-"} />
            <InfoRow label="Route" value={`${bus.from || "-"} → ${bus.to || "-"}`} />
            <InfoRow label="Travel Date" value={booking.date || "-"} />
            <InfoRow label="Departure Time" value={booking.departureTime || "-"} />
            <InfoRow label="Boarding Point" value={booking.from || "-"} />
            <InfoRow label="Dropping Point" value={booking.to || "-"} />
            <InfoRow label="Selected Seats" value={seatCount ? seats.join(", ") : "-"} />
            <InfoRow label="Seat Count" value={String(seatCount)} />
            <InfoRow label="Bus ID" value={busObjectId} mono />
            <InfoRow label="Operator Name" value={bus.operatorName || "-"} />
            <InfoRow label="Operator ID" value={operatorId} mono />
          </div>
        </SectionCard>

        <SectionCard
          title="Payment Details"
          subtitle="Gateway, verification, and settlement metadata"
          icon="💳"
        >
          <div>
            <InfoRow label="Provider" value={payment.provider || "-"} />
            <InfoRow label="Gateway Status" value={payment.state || "-"} />
            <InfoRow label="Transaction ID" value={payment.transactionId || "-"} mono />
            <InfoRow
              label="Customer Reference"
              value={payment.customerReference || "-"}
              mono
            />
            <InfoRow label="Local Payment ID" value={payment.localId || "-"} mono />
            <InfoRow label="Verification Source" value={payment.verificationSource || "-"} />
            <InfoRow label="Verified At" value={formatDate(payment.lastVerifiedAt)} />
            <InfoRow label="Paid At" value={formatDate(payment.paidAt)} />
            <InfoRow label="Expires At" value={formatDate(payment.expiresAt)} />
            <InfoRow label="Redirect URL" value={payment.redirectUrl || "-"} mono />
            <InfoRow label="Short URL" value={payment.shortUrl || "-"} mono />
          </div>
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <SectionCard
          title="Booking Ownership & Creation"
          subtitle="Who owns the booking and who created it"
          icon="🧾"
        >
          <div>
            <InfoRow label="Booked By Name" value={bookedBy.fullName || bookedBy.name || "-"} />
            <InfoRow label="Booked By Email" value={bookedBy.email || "-"} />
            <InfoRow label="Booked By Role" value={bookedBy.role || "-"} />
            <InfoRow label="Booked By ID" value={getObjectId(bookedBy)} mono />
            <InfoRow label="Booking Owner ID" value={bookingOwnerId} mono />
            <InfoRow label="Booking Source" value={booking.isManual ? "Manual / Operator" : "Online / User"} />
          </div>
        </SectionCard>

        <SectionCard
          title="Seat & Passenger Summary"
          subtitle="Quick summary of seat allocations and passenger records"
          icon="🎟️"
        >
          <div>
            <InfoRow label="Selected Seat Count" value={String(seatCount)} />
            <InfoRow label="Passenger Record Count" value={String(passengerCount)} />
            <InfoRow label="Seat Allocation Count" value={String(seatAllocations.length || 0)} />
            <InfoRow
              label="Seat Allocation Genders"
              value={
                seatAllocations.length
                  ? seatAllocations.map((s) => `${s.seat}:${s.gender || "-"}`).join(", ")
                  : "-"
              }
            />
          </div>
        </SectionCard>
      </div>

      <div className="bg-white border rounded-2xl p-5 shadow-sm mt-6">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-900">🧍 Passenger List</h2>
          <p className="text-sm text-gray-500 mt-1">
            Detailed passenger information captured for this booking
          </p>
        </div>

        {passengers.length ? (
          <div className="overflow-auto rounded-xl border">
            <table className="w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border-b px-3 py-3 text-left">Seat</th>
                  <th className="border-b px-3 py-3 text-left">Name</th>
                  <th className="border-b px-3 py-3 text-left">Age</th>
                  <th className="border-b px-3 py-3 text-left">Gender</th>
                </tr>
              </thead>
              <tbody>
                {passengers.map((p, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="border-b px-3 py-3">{p.seat || "-"}</td>
                    <td className="border-b px-3 py-3">{p.name || "-"}</td>
                    <td className="border-b px-3 py-3">{p.age ?? "-"}</td>
                    <td className="border-b px-3 py-3">{p.gender || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-sm">No passenger data available</p>
        )}
      </div>

      <div className="bg-white border rounded-2xl p-5 shadow-sm mt-6">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-900">🔎 Reference & Identifiers</h2>
          <p className="text-sm text-gray-500 mt-1">
            Useful IDs for debugging, support, and payment investigation
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <InfoRow label="Booking ID" value={booking._id || "-"} mono />
            <InfoRow label="User Object ID" value={bookingOwnerId} mono />
            <InfoRow label="Bus Object ID" value={busObjectId} mono />
          </div>
          <div>
            <InfoRow label="Transaction ID" value={payment.transactionId || "-"} mono />
            <InfoRow label="Customer Ref" value={payment.customerReference || "-"} mono />
            <InfoRow label="Local ID" value={payment.localId || "-"} mono />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminBookingView;
